<template>
  <div v-if="hasOrders" class="product-split">
    <div v-on:click="collapsed = !collapsed">
      <span v-if="collapsable" class="collapsible-btn ico-before ico-before-arrow-down" :class="[{ collapsed }]">
      </span>

      <!-- Styles split -->
      <h2>{{ $t('viewSummary.stylesSplit') }}</h2>
    </div>
    <div v-show="showSplit">
      <div class="box-container">
        <div v-for="split in stylesSplit" :key="split.mainGroup" :class="`info-box main-group-${split.mainGroup}`">
          <p>
            {{ $t(`viewSummary.totalQuantity.${split.mainGroup}`) }}<br />
            {{ split.itemCount }}
          </p>
          <p>
            {{ $t(`viewSummary.totalPrice.${split.mainGroup}`) }}<br />
            {{ split.totalPrice ? formatCurrency(currency, split.totalPrice) : $t('viewOrder.not_available') }}
          </p>
          <p>
            {{ $t(`viewSummary.totalPercentage.${split.mainGroup}`) }}<br />
            {{ split.percentage ? formatPercentage(split.percentage, true) : $t('viewOrder.not_available') }}
          </p>
        </div>
      </div>

      <!-- Gender split -->
      <h2>{{ $t('viewSummary.gender') }}</h2>
      <div class="box-container">
        <div v-for="groupName in availableMainGroups" :key="groupName" :class="`info-box main-group-${groupName}`">
          <template v-for="gender in availableGendersPerMainGroup(groupName)" :key="`${groupName}-${gender}`">
            <product-list
              :title="$t(`viewSummary.genderSplits.${groupName}-${genderValueToI18nString(gender)}`)"
              :items="productListForGroupAndGender(groupName, gender)" />
          </template>
        </div>
      </div>
    </div>

    <!-- Total display -->
    <div class="totals-cnt">
      <ul class="totals">
        <li>
          <p>{{ $t(`viewSummary.totalQuantityLabel`) }}</p>
          <span>{{ totalPriceAndQuantity.quantity }}</span>
        </li>
        <li>
          <p>{{ $t(`viewSummary.totalPriceLabel`) }}</p>
          <span :class="{ 'order-total-disable': !totalPriceAndQuantity.price }">{{
            totalPriceAndQuantity.price
              ? formatCurrency(currency, totalPriceAndQuantity.price)
              : $t('viewOrder.not_available')
          }}</span>
        </li>
        <li class="label">
          <p>{{ $t(`viewSummary.totalPriceDisclaimer`) }}</p>
        </li>
      </ul>
    </div>
  </div>

  <!-- Loading -->
  <div v-else-if="loading" class="loader"></div>

  <!-- No data -->
  <div v-else-if="showErrorMessage" class="no-data">
    {{ $t(`viewSummary.noData`) }}
  </div>
</template>

<script lang="ts">
import { DraftsService } from '@/services/drafts.service';
import { OrdersService } from '@/services/orders.service';
import { OrderSummaryDto, ProductMainGroups } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useFormat } from '../../composables/format';
import { mappersMixins } from '../../mixins/mappers.mixins';
import { useUserStore } from '../../stores/user';
import ProductList from './product-list.vue';

export default defineComponent({
  name: 'productmon-split',
  components: {
    'product-list': ProductList,
  },
  props: {
    orderNumbers: {
      type: Array<string>,
      default: [],
    },
    isDraft: {
      type: Boolean,
      default: false,
    },
    collapsable: {
      type: Boolean,
      default: false,
    },
    showErrorMessage: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { selectedAccount } = storeToRefs(useUserStore());
    const { formatCurrency, formatPercentage } = useFormat();

    return { account: selectedAccount, formatCurrency, formatPercentage };
  },
  data() {
    return {
      loading: true,
      error: false,
      ordersService: new OrdersService(),
      draftsService: new DraftsService(),
      summary: [] as OrderSummaryDto[],
      genderOrder: [0 /* unisex */, 1 /* male */, 2 /* female */, 3 /* youth */, 4 /* kids */],
      mainGroupOrder: [ProductMainGroups.shoes, ProductMainGroups.apparel, ProductMainGroups.accessories],
      collapsed: false,
    };
  },
  computed: {
    currency(): string | undefined {
      return this.summary[0]?.currencyCode;
    },
    showSplit(): boolean {
      return this.collapsable ? !this.collapsed : true;
    },
    availableMainGroups(): ProductMainGroups[] {
      const foundMainGroups = this.summary.reduce((acc, item) => {
        if (!acc.includes(item.mainGroup)) {
          acc.push(item.mainGroup);
        }

        return acc;
      }, [] as ProductMainGroups[]);

      return this.mainGroupOrder.filter((groupName) => foundMainGroups.includes(groupName));
    },
    stylesSplit(): any {
      if (this.summary.length === 0) {
        return [];
      }

      const result = this.availableMainGroups.map((mainGroup) => {
        const items = this.summary.filter((item) => item.mainGroup === mainGroup);

        return items.reduce(
          (acc, item) => {
            acc.itemCount += item.itemCount || 0;
            acc.totalPrice += item.totalPrice || 0;

            return acc;
          },
          { itemCount: 0, totalPrice: 0, mainGroup },
        );
      });

      const total = result.reduce((acc, split) => acc + split.totalPrice, 0);

      return result.map((split) => ({
        ...split,
        percentage: total ? split.totalPrice / total : 0,
      }));
    },
    totalPriceAndQuantity(): { quantity: number; price: number } {
      return this.summary.reduce(
        (acc, item) => ({
          quantity: acc.quantity + (item.itemCount || 0),
          price: acc.price + (item.totalPrice || 0),
        }),
        { quantity: 0, price: 0 },
      );
    },
    hasOrders(): boolean {
      // TODO for sales reps, there is no account trading currency
      // available. When we do not know the currency, we're showing the
      // same error as if we hadn't been able to load the orders at all
      // for now.
      return this.summary.length !== 0 && !!this.currency;
    },
  },
  mounted() {
    this.fetchSummary();
  },
  methods: {
    ...mappersMixins,
    async fetchSummary() {
      try {
        this.loading = true;

        if (this.orderNumbers.length === 0) {
          throw new Error('No orders to fetch');
        }

        const summaryValidation = this.isDraft
          ? await this.draftsService.getOrderSummary(this.account.id, this.orderNumbers)
          : await this.ordersService.getOrderSummary(this.account.id, this.orderNumbers);

        if (summaryValidation.model) {
          this.summary = summaryValidation.model;
        } else {
          this.error = true;
        }
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    handleBackButton() {
      this.$router.go(-1);
    },
    availableGendersPerMainGroup(mainGroup: string) {
      const foundGenders = this.summary
        .filter((item) => item.mainGroup === mainGroup)
        .reduce((acc, item) => {
          if (!acc.includes(item.gender)) {
            acc.push(item.gender);
          }

          return acc;
        }, [] as number[]);

      return this.genderOrder.filter((gender) => foundGenders.includes(gender));
    },
    productListForGroupAndGender(groupName: string, gender: number): { name: string; amount: number }[] {
      const items = (this.summary as OrderSummaryDto[]).filter(
        (item) => item.mainGroup === groupName && item.gender === gender,
      );
      const itemNames = [...new Set(items.map((item) => item.name))];

      return itemNames.map((itemName) => ({
        name: itemName,
        amount: items.reduce((acc, item) => {
          if (item.name === itemName) {
            acc += item.itemCount;
          }

          return acc;
        }, 0),
      }));
    },
  },
});
</script>
<style lang="scss">
.product-split {
  h2 {
    margin-bottom: 10px;
    margin-top: 15px;
    padding-left: 20px;
    font-size: 20px;
    font-weight: 400;
    line-height: 30px;
    letter-spacing: 0em;
    text-align: left;
  }

  .box-container {
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
  }

  .info-box {
    padding: 26px 20px;
    text-align: left;
    background-color: var(--primary-bg-color);
    border-radius: 8px;
    width: 100%;
    font-size: 14px;
    color: var(--primary-text-color);

    p {
      margin-top: 0;
    }

    .total-box:not(:first-child) {
      border-top: 1px solid var(--border-color);
    }
  }

  .main-group-apparel {
    padding-left: 72px;
    background-repeat: no-repeat;
    background-image: url('@/assets/img/apparel.svg');
    background-position: 26px 33px;
  }

  .main-group-shoes {
    padding-left: 72px;
    background-repeat: no-repeat;
    background-image: url('@/assets/img/shoe.svg');
    background-position: 27px 30px;
  }

  .main-group-accessories {
    padding-left: 72px;
    background-repeat: no-repeat;
    background-image: url('@/assets/img/accessories.svg');
    background-position: 30px 34px;
  }

  .no-data {
    margin: 40px;
  }

  .total-box {
    display: flex;
    justify-content: space-between;
    padding: 20px;
    font-size: 16px;
    background-color: var(--bg-remark-color); // Todo use var for dark mode

    .price {
      font-weight: bold;
    }
  }

  .total-disclaimer {
    margin-top: 12px;
    padding: 0 6px;
  }

  .info-box ~ .info-box {
    margin-left: 12px;
  }

  .title-box {
    font-size: 20px;
    font-weight: 400;
    line-height: 30px;
    letter-spacing: 0em;
    text-align: left;
    display: flex;
    justify-content: space-between;

    .back-icon {
      background-color: initial;
      background-image: url('@/assets/img/back-icon.svg');
      border: none;
      cursor: pointer;
      height: 42px;
      margin: -6px 0;
      padding: 0;
      width: 42px;
    }
  }

  .panel-heading {
    padding: 0;
    border: 0;
  }

  .panel-title > a,
  .panel-title > a:active {
    display: block;
    padding: 15px;
    color: var(--primary-text-remark-color);
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 1px;
    word-spacing: 3px;
    text-decoration: none;
  }

  .panel-heading a:before {
    font-family: 'Glyphicons Halflings';
    content: '\e114';
    float: right;
    transition: all 0.5s;
  }

  .panel-heading.active a:before {
    -webkit-transform: rotate(180deg);
    -moz-transform: rotate(180deg);
    transform: rotate(180deg);
  }

  .ico-before {
    float: right;
  }

  .collapsible-btn {
    padding: 5 0 0 0;
    display: flex;
    align-items: center;
    position: relative;
    flex: 1;
    cursor: pointer;

    &:before {
      content: ' ';
      background-size: 15px;
      height: 15px;
      width: 15px;
      left: 15px;
      transition: all 0.2s linear;
    }

    &.collapsed:before {
      transform: rotate(180deg);
      margin-top: -1px;
    }
  }
}

.no-data {
  padding: 40px 0;
}
</style>
